import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiService from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

// Recall-System (Wiedereinbestellung): Fällig-Liste mit Versand/Anruf-Aufgaben,
// Typ-Einstellungen mit Prozedur-Zuordnung, Erfolgs-Statistik.

type Tab = 'due' | 'settings' | 'stats';

interface DueEntry {
  id: number;
  patient_id: number;
  patient_name: string;
  patient_email: string | null;
  patient_phone: string | null;
  recall_type: string;
  type_label: string;
  due_date: string;
  overdue: boolean;
  status: string;
  attempts: number;
  max_attempts: number;
  last_notified_at: string | null;
  consent: 'granted' | 'denied' | 'revoked' | 'never_asked';
  consent_details: {
    status: string;
    granted_at: string | null;
    version: string | null;
    revoked_at: string | null;
  };
}

interface RecallType {
  key: string;
  label: string;
  interval_months: number;
  procedure_ids: number[];
  procedure_ids_from_defaults?: boolean;
  enabled: boolean;
}

interface RecallSettings {
  enabled: boolean;
  types: RecallType[];
  retry_after_weeks: number;
  max_attempts: number;
}

interface RecallStats {
  period_days: number;
  sent: number;
  booked: number;
  conversion: number | null;
  open_schedules: number;
  due_without_consent: number;
}

interface ProcedureOption {
  id: number;
  label: string;
}

const resolveProcedureLabel = (p: any): string => {
  if (typeof p.name === 'string') return p.name;
  if (p.name && typeof p.name === 'object') return p.name.de || p.name.en || p.name.es || Object.values(p.name)[0] as string;
  return `#${p.id}`;
};

export const RecallsPage: React.FC = () => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [tab, setTab] = useState<Tab>('due');
  const [due, setDue] = useState<DueEntry[]>([]);
  const [settings, setSettings] = useState<RecallSettings | null>(null);
  const [stats, setStats] = useState<RecallStats | null>(null);
  const [procedures, setProcedures] = useState<ProcedureOption[]>([]);
  const [statsDays, setStatsDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const loadAll = useCallback(async () => {
    try {
      const [dueResp, settingsResp, procsResp] = await Promise.all([
        apiService.getRecallDue(30),
        apiService.getRecallSettings(),
        apiService.getProcedures(true),
      ]);
      if (dueResp.data?.success) setDue(dueResp.data.data);
      if (settingsResp.data?.success) setSettings(settingsResp.data.data);
      const procs = (procsResp.data?.data || []) as any[];
      setProcedures(procs.map(p => ({ id: p.id, label: resolveProcedureLabel(p) })));
    } catch {
      addToast(t('recalls.errorLoad'), 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast, t]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    apiService.getRecallStats(statsDays)
      .then(resp => { if (resp.data?.success) setStats(resp.data.data); })
      .catch(() => { /* Statistik ist nicht kritisch */ });
  }, [statsDays, due]);

  const handleSend = async (entry: DueEntry) => {
    setBusyId(entry.id);
    try {
      const resp = await apiService.sendRecall(entry.id);
      const result = resp.data?.data;
      if (result?.status === 'sent') addToast(t('recalls.sentToast'), 'success');
      else if (result?.skip_reason === 'already_booked') addToast(t('recalls.sentBookedToast'), 'success');
      await loadAll();
    } catch (e: any) {
      const reasonKey = e?.response?.data?.data?.skip_reason;
      const reason = reasonKey === 'no_marketing_consent' ? t('recalls.reasonNoConsent')
        : reasonKey === 'no_email' ? t('recalls.reasonNoEmail')
        : t('recalls.reasonOther');
      addToast(t('recalls.sendBlockedToast', { reason }), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleDismiss = async (entry: DueEntry) => {
    setBusyId(entry.id);
    try {
      await apiService.dismissRecall(entry.id);
      addToast(t('recalls.dismissedToast'), 'success');
      await loadAll();
    } catch {
      addToast(t('recalls.errorSave'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const handleRun = async () => {
    setRunning(true);
    try {
      const resp = await apiService.runRecalls(false);
      const s = resp.data?.data || {};
      addToast(t('recalls.runResult', { sent: s.sent ?? 0, skipped: s.skipped ?? 0, booked: s.booked ?? 0 }), 'success');
      await loadAll();
    } catch {
      addToast(t('recalls.errorSave'), 'error');
    } finally {
      setRunning(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const resp = await apiService.updateRecallSettings(settings);
      if (resp.data?.success) {
        setSettings(resp.data.data);
        addToast(t('recalls.saved'), 'success');
      } else {
        addToast(resp.data?.message || t('recalls.errorSave'), 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || t('recalls.errorSave'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const updateType = (key: string, patch: Partial<RecallType>) => {
    if (!settings) return;
    setSettings({
      ...settings,
      types: settings.types.map(ty => (ty.key === key ? { ...ty, ...patch } : ty)),
    });
  };

  // Consent-Badge + DSGVO-Beleg: bei erteiltem Opt-in steht daneben, WANN es
  // erteilt wurde (inkl. Einwilligungstext-Version) und dass kein Widerruf
  // vorliegt; bei Widerruf das Widerrufs-Datum.
  const consentBadge = (entry: DueEntry) => {
    const map: Record<DueEntry['consent'], { label: string; cls: string }> = {
      granted: { label: t('recalls.consentGranted'), cls: 'bg-green-100 text-green-800' },
      denied: { label: t('recalls.consentDenied'), cls: 'bg-amber-100 text-amber-800' },
      revoked: { label: t('recalls.consentRevoked'), cls: 'bg-red-100 text-red-800' },
      never_asked: { label: t('recalls.consentNeverAsked'), cls: 'bg-gray-100 text-gray-700' },
    };
    const m = map[entry.consent];
    const d = entry.consent_details;
    return (
      <div>
        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${m.cls}`}>{m.label}</span>
        {entry.consent === 'granted' && d?.granted_at && (
          <div className="text-[11px] text-gray-500 mt-1">
            {t('recalls.consentGrantedAt', {
              date: new Date(d.granted_at).toLocaleDateString(),
              version: d.version || 'v1',
            })}
            <br />
            {t('recalls.consentNoRevocation')}
          </div>
        )}
        {entry.consent === 'revoked' && d?.revoked_at && (
          <div className="text-[11px] text-red-600 mt-1">
            {t('recalls.consentRevokedAt', { date: new Date(d.revoked_at).toLocaleDateString() })}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="p-6 text-gray-500">{t('recalls.loading')}</div>;
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: 'due', label: t('recalls.tabDue') },
    { id: 'settings', label: t('recalls.tabSettings') },
    { id: 'stats', label: t('recalls.tabStats') },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('recalls.title')}</h1>
        <p className="text-sm text-gray-600">{t('recalls.subtitle')}</p>
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          {tabs.map(td => (
            <button
              key={td.id}
              onClick={() => setTab(td.id)}
              className={`py-2 px-1 border-b-2 text-sm font-medium transition-colors ${
                tab === td.id
                  ? 'border-brand-primary text-brand-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {td.label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'due' && (
        <div className="space-y-4">
          {isAdmin && (
            <div className="flex justify-end">
              <button
                onClick={handleRun}
                disabled={running}
                className="px-4 py-2 bg-brand-primary text-white rounded font-semibold text-sm disabled:opacity-50 hover:bg-opacity-90"
              >
                {running ? t('recalls.running') : t('recalls.runNow')}
              </button>
            </div>
          )}

          {due.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">{t('recalls.dueEmpty')}</div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                    <th className="px-4 py-3">{t('recalls.colPatient')}</th>
                    <th className="px-4 py-3">{t('recalls.colType')}</th>
                    <th className="px-4 py-3">{t('recalls.colDue')}</th>
                    <th className="px-4 py-3">{t('recalls.colAttempts')}</th>
                    <th className="px-4 py-3">{t('recalls.colConsent')}</th>
                    <th className="px-4 py-3 text-right">{t('recalls.colActions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {due.map(entry => {
                    const mailable = entry.consent === 'granted' && !!entry.patient_email;
                    return (
                      <tr key={entry.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{entry.patient_name}</div>
                          <div className="text-xs text-gray-500">
                            {entry.patient_email || t('recalls.noEmail')}{entry.patient_phone ? ` · ${entry.patient_phone}` : ''}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">{entry.type_label}</td>
                        <td className="px-4 py-3">
                          <span className={entry.overdue ? 'text-red-600 font-semibold' : 'text-gray-700'}>
                            {entry.due_date}
                          </span>
                          {entry.overdue && (
                            <span className="ml-2 text-xs text-red-500">({t('recalls.overdue')})</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {entry.attempts}/{entry.max_attempts}
                          {entry.last_notified_at && (
                            <div className="text-xs text-gray-400">
                              {t('recalls.lastNotified', { date: new Date(entry.last_notified_at).toLocaleDateString() })}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">{consentBadge(entry)}</td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {mailable ? (
                            <button
                              onClick={() => handleSend(entry)}
                              disabled={busyId === entry.id}
                              className="px-3 py-1.5 bg-brand-primary text-white rounded text-xs font-semibold disabled:opacity-50 hover:bg-opacity-90"
                            >
                              {busyId === entry.id ? t('recalls.sending') : t('recalls.sendNow')}
                            </button>
                          ) : (
                            <span className="px-3 py-1.5 bg-amber-50 text-amber-800 rounded text-xs font-semibold" title={t('recalls.callTaskHint')}>
                              {t('recalls.callTask')}
                            </span>
                          )}
                          <button
                            onClick={() => handleDismiss(entry)}
                            disabled={busyId === entry.id}
                            className="ml-2 px-3 py-1.5 border border-gray-300 text-gray-600 rounded text-xs font-semibold disabled:opacity-50 hover:bg-gray-50"
                          >
                            {t('recalls.dismiss')}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab === 'settings' && settings && (
        <div className="max-w-3xl space-y-6">
          <section className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-5">
            <h2 className="text-lg font-bold text-gray-900">{t('recalls.settingsTitle')}</h2>
            <p className="text-sm text-gray-600">{t('recalls.consentNote')}</p>

            <div className="flex items-center space-x-2">
              <input
                id="recall-enabled"
                type="checkbox"
                checked={settings.enabled}
                onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
                disabled={!isAdmin}
                className="h-4 w-4"
              />
              <label htmlFor="recall-enabled" className="text-sm text-gray-700">{t('recalls.enabled')}</label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t('recalls.retryAfterWeeks')}</label>
                <input
                  type="number" min={1} max={26}
                  value={settings.retry_after_weeks}
                  onChange={(e) => setSettings({ ...settings, retry_after_weeks: parseInt(e.target.value || '0', 10) })}
                  disabled={!isAdmin}
                  className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-light-blue outline-none disabled:bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t('recalls.maxAttempts')}</label>
                <input
                  type="number" min={1} max={5}
                  value={settings.max_attempts}
                  onChange={(e) => setSettings({ ...settings, max_attempts: parseInt(e.target.value || '0', 10) })}
                  disabled={!isAdmin}
                  className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-light-blue outline-none disabled:bg-gray-50"
                />
              </div>
            </div>
          </section>

          <section className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-5">
            <h2 className="text-lg font-bold text-gray-900">{t('recalls.typesTitle')}</h2>

            {settings.types.map(ty => (
              <div key={ty.key} className="border border-gray-200 rounded-lg p-4 space-y-3">
                <div className="flex flex-wrap items-center gap-4">
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={ty.enabled}
                      onChange={(e) => updateType(ty.key, { enabled: e.target.checked })}
                      disabled={!isAdmin}
                      className="h-4 w-4"
                    />
                    {t('recalls.typeEnabled')}
                  </label>
                  <div className="flex-1 min-w-[180px]">
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t('recalls.typeLabel')}</label>
                    <input
                      type="text"
                      value={ty.label}
                      onChange={(e) => updateType(ty.key, { label: e.target.value })}
                      disabled={!isAdmin}
                      className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-light-blue outline-none disabled:bg-gray-50"
                    />
                  </div>
                  <div className="w-36">
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t('recalls.typeInterval')}</label>
                    <input
                      type="number" min={1} max={36}
                      value={ty.interval_months}
                      onChange={(e) => updateType(ty.key, { interval_months: parseInt(e.target.value || '0', 10) })}
                      disabled={!isAdmin}
                      className="w-full p-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-light-blue outline-none disabled:bg-gray-50"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">{t('recalls.typeProcedures')}</label>
                  <p className="text-[11px] text-gray-500 mb-2">{t('recalls.typeProceduresHint')}</p>
                  <div className="flex flex-wrap gap-2">
                    {procedures.map(p => {
                      const selected = ty.procedure_ids.includes(p.id);
                      return (
                        <button
                          key={p.id}
                          type="button"
                          disabled={!isAdmin}
                          onClick={() => updateType(ty.key, {
                            procedure_ids: selected
                              ? ty.procedure_ids.filter(id => id !== p.id)
                              : [...ty.procedure_ids, p.id],
                          })}
                          className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                            selected
                              ? 'bg-brand-primary text-white border-brand-primary'
                              : 'bg-white text-gray-600 border-gray-300 hover:border-gray-500'
                          } disabled:opacity-60`}
                        >
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                  {ty.enabled && ty.procedure_ids.length === 0 && (
                    <p className="mt-2 text-xs text-amber-700">{t('recalls.noProceduresWarning')}</p>
                  )}
                  {ty.procedure_ids_from_defaults && (
                    <p className="mt-2 text-xs text-gray-500">{t('recalls.defaultMappingHint')}</p>
                  )}
                </div>
              </div>
            ))}

            {isAdmin && (
              <div className="flex justify-end">
                <button
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="px-5 py-2 bg-brand-primary text-white rounded font-semibold disabled:opacity-50 hover:bg-opacity-90"
                >
                  {saving ? t('settings.saving') : t('common.save')}
                </button>
              </div>
            )}
          </section>
        </div>
      )}

      {tab === 'stats' && (
        <div className="max-w-3xl space-y-4">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-600">{t('recalls.statsPeriod')}:</span>
            {[30, 90, 365].map(d => (
              <button
                key={d}
                onClick={() => setStatsDays(d)}
                className={`px-3 py-1.5 rounded text-sm font-medium border ${
                  statsDays === d
                    ? 'bg-brand-primary text-white border-brand-primary'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-gray-500'
                }`}
              >
                {t(d === 30 ? 'recalls.days30' : d === 90 ? 'recalls.days90' : 'recalls.days365')}
              </button>
            ))}
          </div>

          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { label: t('recalls.statSent'), value: stats.sent },
                { label: t('recalls.statBooked'), value: stats.booked },
                { label: t('recalls.statConversion'), value: stats.conversion !== null ? `${stats.conversion}%` : '—' },
                { label: t('recalls.statOpen'), value: stats.open_schedules },
                { label: t('recalls.statNoConsent'), value: stats.due_without_consent },
              ].map(card => (
                <div key={card.label} className="bg-white rounded-lg shadow p-5">
                  <div className="text-2xl font-bold text-gray-900">{card.value}</div>
                  <div className="text-xs text-gray-500 mt-1">{card.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RecallsPage;
